import { Fragment } from 'react'
import type { SpreadLayout } from './valueDesignLayout'
import {
  painLabel, reliefLabel, outcomeLabel, creatorLabel,
} from './valueDesignLayout'

interface Props {
  layout: SpreadLayout
  /** Reliever index → pain indices it tackles */
  byReliever: number[][]
  /** Creator index → outcome indices it delivers */
  byCreator: number[][]
  /** Number of actual pain rows (≤ maxPainRows) */
  painCount: number
  /** Number of actual reliever rows (≤ maxPainRows) */
  relieverCount: number
  /** Number of actual outcome rows */
  outcomeCount: number
  /** Number of actual creator rows */
  creatorCount: number

  /** Currently hovered row's stable id, e.g. "P1", "R2", "O3", "G1" */
  hoveredId: string | null
  /** Set of ids linked to the hovered row (and therefore highlighted) */
  linkedSet: Set<string>
}

/**
 * Renders the SVG ribbon overlay between the customer and solution columns.
 * Curves use cubic beziers anchored on the inner edges of each row;
 * coordinates come from the layout helper so rows stay in lockstep.
 */
export function ConnectorLane({
  layout, byReliever, byCreator,
  painCount, relieverCount, outcomeCount, creatorCount,
  hoveredId, linkedSet,
}: Props) {
  // SVG viewBox matches the lane's pixel width (LANE_W from layout) so the
  // SVG renders at 1:1 — anchors stay round, curves stay symmetric.
  const W = 96
  const X_LEFT = 6
  const X_RIGHT = W - X_LEFT

  // Pain ↔ reliever links (gold)
  const painLinks: Array<{ from: string; to: string; y1: number; y2: number }> = []
  byReliever.forEach((painIdxs, j) => {
    painIdxs.forEach(i => {
      painLinks.push({
        from: painLabel(i),
        to:   reliefLabel(j),
        y1:   layout.painY(i),
        y2:   layout.painY(j),
      })
    })
  })

  // Outcome ↔ creator links (teal)
  const gainLinks: Array<{ from: string; to: string; y1: number; y2: number }> = []
  byCreator.forEach((outIdxs, j) => {
    outIdxs.forEach(i => {
      gainLinks.push({
        from: outcomeLabel(i),
        to:   creatorLabel(j),
        y1:   layout.outcomeY(i),
        y2:   layout.outcomeY(j),
      })
    })
  })

  const isLinkActive = (from: string, to: string) =>
    hoveredId === from || hoveredId === to ||
    linkedSet.has(from) || linkedSet.has(to)

  const isAnchorActive = (id: string) =>
    hoveredId === id || linkedSet.has(id)

  const renderPath = (
    key: string,
    y1: number, y2: number,
    color: 'gold' | 'teal',
    active: boolean,
  ) => {
    // Slight vertical fanning for multiple lines from same anchor:
    // pull the start/end y by 0.5 px towards the destination so two
    // links from the same row don't lay perfectly on top of each other.
    const dy = y2 > y1 ? 1 : y2 < y1 ? -1 : 0
    const ay1 = y1 + dy
    const ay2 = y2 - dy
    const cx = (X_LEFT + X_RIGHT) / 2
    const d  = `M ${X_LEFT} ${ay1} C ${cx} ${ay1}, ${cx} ${ay2}, ${X_RIGHT} ${ay2}`
    const stroke = active
      ? 'var(--color-accent-500)'
      : color === 'gold' ? 'var(--color-solution)' : 'var(--color-market)'
    return (
      <path
        key={key}
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth={active ? 2 : 1.4}
        opacity={active ? 1 : 0.32}
        style={{ transition: 'opacity 140ms ease, stroke-width 140ms ease, stroke 140ms ease' }}
      />
    )
  }

  const renderAnchor = (id: string, x: number, y: number, color: 'gold' | 'teal', empty: boolean) => {
    const active = isAnchorActive(id)
    const stroke = active
      ? 'var(--color-accent-500)'
      : color === 'gold' ? 'var(--color-solution)' : 'var(--color-market)'
    const fill = active ? 'var(--color-accent-500)' : 'var(--color-bg, #fcfcf9)'
    return (
      <circle
        key={id}
        cx={x} cy={y} r={3}
        fill={fill}
        stroke={stroke}
        strokeWidth={1.4}
        strokeDasharray={empty ? '1.5 1.5' : undefined}
        opacity={empty && !active ? 0.45 : 1}
        style={{ transition: 'fill 140ms ease, stroke 140ms ease' }}
      />
    )
  }

  return (
    <div
      className="relative pointer-events-none"
      style={{
        gridColumn: 2,
        gridRow: '1 / -1',
        height: '100%',
      }}
      aria-hidden
    >
      <svg
        viewBox={`0 0 ${W} ${layout.totalH}`}
        preserveAspectRatio="xMidYMin meet"
        className="absolute inset-0 w-full h-full overflow-visible"
      >
        {/* Pain↔reliever curves */}
        {painLinks.map((l, idx) => (
          <Fragment key={`pl-${idx}-${l.from}-${l.to}`}>
            {renderPath(`pl-${idx}`, l.y1, l.y2, 'gold', isLinkActive(l.from, l.to))}
          </Fragment>
        ))}
        {/* Outcome↔creator curves */}
        {gainLinks.map((l, idx) => (
          <Fragment key={`gl-${idx}-${l.from}-${l.to}`}>
            {renderPath(`gl-${idx}`, l.y1, l.y2, 'teal', isLinkActive(l.from, l.to))}
          </Fragment>
        ))}

        {/* Anchors — pains (left), relievers (right) */}
        {Array.from({ length: layout.maxPainRows }).map((_, i) => {
          const y = layout.painY(i)
          const leftId  = painLabel(i)
          const rightId = reliefLabel(i)
          const leftEmpty  = i >= painCount
          const rightEmpty = i >= relieverCount
          return (
            <Fragment key={`p-anc-${i}`}>
              {renderAnchor(leftId,  X_LEFT,  y, 'gold', leftEmpty)}
              {renderAnchor(rightId, X_RIGHT, y, 'gold', rightEmpty)}
            </Fragment>
          )
        })}
        {/* Anchors — outcomes (left), creators (right) */}
        {Array.from({ length: layout.maxOutcomeRows }).map((_, i) => {
          const y = layout.outcomeY(i)
          const leftId  = outcomeLabel(i)
          const rightId = creatorLabel(i)
          const leftEmpty  = i >= outcomeCount
          const rightEmpty = i >= creatorCount
          return (
            <Fragment key={`o-anc-${i}`}>
              {renderAnchor(leftId,  X_LEFT,  y, 'teal', leftEmpty)}
              {renderAnchor(rightId, X_RIGHT, y, 'teal', rightEmpty)}
            </Fragment>
          )
        })}
      </svg>
    </div>
  )
}
